// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  DateRangePicker,
  DropdownMenu,
  IconName,
  Label,
  MultiSelect,
  Text,
  TextField,
} from '@usealloy/component-library';
import { flowOptions, initialFilterState } from './constants';
import { getFilterOptions } from './helpers';
import { StyledLinkText } from './styles';
import ExportCSV from '../ExportCSV';

type FilterStateType = typeof initialFilterState;
type SetStateCallback = (prevState: FilterStateType) => FilterStateType;

interface TransactionHistoryFiltersProps {
  data: any;
  filterState: FilterStateType;
  setFilterState: (arg: SetStateCallback | FilterStateType) => void;
  extEntityId?: string;
  filteredTransactions: any[];
  CSVColumns: any[];
  columnVisibility?: Record<string, boolean>;
}

export const TransactionHistoryFilters = (
  {
    data, filterState, setFilterState, extEntityId, filteredTransactions, CSVColumns,
    columnVisibility,
  }: TransactionHistoryFiltersProps,
) => {
  const [shownFacets, setShownFacets] = useState({
    processMethod: false,
    amount: false,
    category: false,
    productName: false,
    interactionPoint: false,
  });

  const processMethodOptions = useMemo(() => (getFilterOptions('process_method', data)), [data]);
  const categoryOptions = useMemo(() => (getFilterOptions('category', data)), [data]);
  const productNameOptions = useMemo(() => (getFilterOptions('source_bank_product_name', data)), [data]);
  const interactionPointOptions = useMemo(() => (getFilterOptions('interaction_point', data)), [data]);

  const [amountDisplay, setAmountDisplay] = useState(['', '']);

  return (
    <Box direction="column" style={{ padding: 'var(--cl-space-3) 0' }} gap={3}>
      <Box direction="row" justifyContent="flex-start" gap={3}>
        <Text size={3} weight={700}>Filter By</Text>
        <StyledLinkText onClick={() => { setFilterState(initialFilterState); setAmountDisplay(['', '']); }}>Clear Filters</StyledLinkText>
      </Box>
      <Box direction="row" justifyContent="space-between" alignItems="flex-end">
        <Box direction="row" justifyContent="flex-start" alignItems="flex-end">
          <DateRangePicker
            label="Date Range"
            style={{ maxWidth: '250px', justifyContent: 'center' }}
            allowClear
            onChange={value => {
              setFilterState(oldState => (
                { ...oldState, dateRange: value }
              ));
            }}
            value={filterState.dateRange}
          />
          <MultiSelect
            isMulti={false}
            label="Flow"
            style={{ maxWidth: '180px' }}
            options={flowOptions}
            onChange={(value: { label: string; value: string }) => {
              setFilterState(oldState => (
                { ...oldState, flow: value }
              ));
            }}
            selectedOptions={filterState.flow}
          />
          {shownFacets.processMethod && (
            <MultiSelect
              isMulti={false}
              label="Process Method"
              style={{ maxWidth: '180px' }}
              options={processMethodOptions}
              placeholder="All"
              onChange={(value: { label: string; value: string }) => {
                setFilterState(oldState => (
                  { ...oldState, processMethod: value }
                ));
              }}
              selectedOptions={filterState.processMethod}
            />
          )}
          {shownFacets.amount && (
            <Box direction="column" style={{ maxWidth: 200 }} alignItems="flex-start" gap={2}>
              <Label>Amount</Label>
              <Box direction="row">
                <TextField
                  type="number"
                  value={amountDisplay[0]}
                  onChange={({ target: { value } }) => {
                    setAmountDisplay(oldState => ([value, oldState[1]]));
                  }}
                  onBlur={({ target: { value } }) => {
                    setFilterState(oldState => ({
                      ...oldState,
                      amount: [value, oldState.amount[1]],
                    }));
                  }}
                  onKeyPress={({ key }) => {
                    if (key === 'Enter') {
                      setFilterState(oldState => ({
                        ...oldState,
                        amount: [amountDisplay[0], oldState.amount[1]],
                      }));
                    }
                  }}
                />
                -
                <TextField
                  type="number"
                  value={amountDisplay[1]}
                  onChange={({ target: { value } }) => {
                    setAmountDisplay(oldState => ([oldState[0], value]));
                  }}
                  onBlur={({ target: { value } }) => {
                    setFilterState(oldState => ({
                      ...oldState,
                      amount: [oldState.amount[0], value],
                    }));
                  }}
                  onKeyPress={({ key }) => {
                    if (key === 'Enter') {
                      setFilterState(oldState => ({
                        ...oldState,
                        amount: [oldState.amount[0], amountDisplay[1]],
                      }));
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          {shownFacets.category && (
            <MultiSelect
              isMulti={false}
              label="Category"
              style={{ maxWidth: '180px' }}
              options={categoryOptions}
              placeholder="All"
              onChange={(value: { label: string; value: string }) => {
                setFilterState(oldState => (
                  { ...oldState, category: value }
                ));
              }}
              selectedOptions={filterState.category}
            />
          )}
          {shownFacets.productName && (
            <MultiSelect
              isMulti={false}
              label="Product Name"
              style={{ maxWidth: '180px' }}
              options={productNameOptions}
              placeholder="All"
              onChange={(value: { label: string; value: string }) => {
                setFilterState(oldState => (
                  { ...oldState, productName: value }
                ));
              }}
              selectedOptions={filterState.productName}
            />
          )}
          {shownFacets.interactionPoint && (
            <MultiSelect
              isMulti={false}
              label="Interaction Point"
              style={{ maxWidth: '180px' }}
              options={interactionPointOptions}
              placeholder="All"
              onChange={(value: { label: string; value: string }) => {
                setFilterState(oldState => (
                  { ...oldState, interactionPoint: value }
                ));
              }}
              selectedOptions={filterState.interactionPoint}
            />
          )}
          {Object.values(shownFacets).some(value => !value) && (
            <DropdownMenu trigger={<Button icon={IconName.Add} />}>
              {!shownFacets.processMethod && processMethodOptions.length > 0 && (
                <DropdownMenu.Item
                  onSelect={() => {
                    setShownFacets(oldState => ({ ...oldState, processMethod: true }));
                  }}
                >
                  Process Method
                </DropdownMenu.Item>
              )}
              {!shownFacets.amount && (
                <DropdownMenu.Item
                  onSelect={() => {
                    setShownFacets(oldState => ({ ...oldState, amount: true }));
                  }}
                >
                  Amount
                </DropdownMenu.Item>
              )}
              {!shownFacets.category && categoryOptions.length > 0 && (
                <DropdownMenu.Item
                  onSelect={() => {
                    setShownFacets(oldState => ({ ...oldState, category: true }));
                  }}
                >
                  Category
                </DropdownMenu.Item>
              )}
              {!shownFacets.productName && productNameOptions.length > 0 && (
                <DropdownMenu.Item
                  onSelect={() => {
                    setShownFacets(oldState => ({ ...oldState, productName: true }));
                  }}
                >
                  Product Name
                </DropdownMenu.Item>
              )}
              {!shownFacets.interactionPoint && interactionPointOptions.length > 0 && (
                <DropdownMenu.Item
                  onSelect={() => {
                    setShownFacets(oldState => ({ ...oldState, interactionPoint: true }));
                  }}
                >
                  Interaction Point
                </DropdownMenu.Item>
              )}
            </DropdownMenu>
          )}
        </Box>
        <ExportCSV
          title={`${extEntityId ? `${extEntityId}_` : ''}transactions_${moment().format('YYYY_MM_DD')}.csv`}
          columns={CSVColumns}
          transactions={filteredTransactions}
          columnVisibility={columnVisibility}
          useCLButton
        />
      </Box>
    </Box>
  );
};
